import { inject, Injectable } from '@angular/core';
import { DisplayGrid, GridsterConfig, GridsterItemConfig, GridType } from 'angular-gridster2';
import { Observable, Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { TopShareholdersComponent } from '../components/top-shareholders/top-shareholders.component';
import { TopTransactionsComponent } from '../components/top-transactions/top-transactions.component';
import { ChatControlComponent } from '../components/widgets/chat-control/chat-control.component';
import { ChatComponent } from '../components/widgets/chat/chat.component';
import { DeliveryMethodComponent } from '../components/widgets/delivery-method/delivery-method.component';
import { DeliveryMethodsStatisticComponent } from '../components/widgets/delivery-methods-statistic/delivery-methods-statistic.component';
import { DmiIntegrationComponent } from '../components/widgets/dmi-integration/dmi-integration.component';
import { FactsAndFiguresComponent } from '../components/widgets/facts-and-figures/facts-and-figures.component';
import { FavoritesListComponent } from '../components/widgets/favorites-list/favorites-list.component';
import { FileSharingComponent } from '../components/widgets/file-sharing/file-sharing.component';
import { GeneralMeetingAgendaComponent } from '../components/widgets/general-meeting-agenda/general-meeting-agenda.component';
import { GeneralMeetingInfoComponent } from '../components/widgets/general-meeting-info/general-meeting-info.component';
import { GeneralMeetingRegistrationComponent } from '../components/widgets/general-meeting-registration/general-meeting-registration.component';
import { GeneralMeetingSettingsComponent } from '../components/widgets/general-meeting-settings/general-meeting-settings.component';
import { InfomaterialComponent } from '../components/widgets/infomaterial/infomaterial.component';
import { LiveStreamVideoComponent } from '../components/widgets/live-stream-widgets/live-stream-video/live-stream-video.component';
import { LiveStreamComponent } from '../components/widgets/live-stream-widgets/live-stream/live-stream.component';
import { LiveVotingComponent } from '../components/widgets/live-voting/live-voting.component';
import { MarkdownComponent } from '../components/widgets/markdown/markdown.component';
import { PendingTransactionsComponent } from '../components/widgets/pending-transactions/pending-transactions.component';
import { PersonalInfoComponent } from '../components/widgets/personal-info/personal-info.component';
import { PresenceComponent } from '../components/widgets/presence/presence.component';
import { ShareholderCategoriesComponent } from '../components/widgets/shareholder-categories/shareholder-categories.component';
import { ShareholdersDomicileComponent } from '../components/widgets/shareholders-domicile/shareholders-domicile.component';
import { TopShareholdersChartComponent } from '../components/widgets/top-shareholders-chart/top-shareholders-chart.component';
import { TotalDispoPercentComponent } from '../components/widgets/total-dispo-percent/total-dispo-percent.component';
import { TotalDispoComponent } from '../components/widgets/total-dispo/total-dispo.component';
import { TotalRegisteredPercentComponent } from '../components/widgets/total-registered-percent/total-registered-percent.component';
import { TotalRegisteredComponent } from '../components/widgets/total-registered/total-registered.component';
import { TotalShareholdersComponent } from '../components/widgets/total-shareholders/total-shareholders.component';
import { VirtualSpeakersDeskComponent } from '../components/widgets/virtual-speakers-desk/virtual-speakers-desk.component';
import { VotingMonitorComponent } from '../components/widgets/voting-monitor/voting-monitor.component';
import { DashboardCardConfigModel } from '../models/dashboard-card-config.model';
import { DashboardCardModel } from '../models/dashboard-card.model';
import { DashboardConfigurationModel } from '../models/dashboard-configuration.model';
import { DashboardComponentTypeEnum } from '../models/enums/dashboard-component.enum';
import { DashboardEnum } from '../models/enums/dashboard.enum';
import { LayoutSettingsModel } from '../models/layout-settings.model';
import { DictionaryType } from '../models/types/dictionary.type';
import { OptionalType } from '../models/types/optional.type';
import { LayoutSettingsService } from './layout-settings.service';
import { MarkdownComponentService } from './markdown-component.service';
import { NotificationService } from './notification.service';
import { RolesResource } from './resources/roles.resource';

@Injectable({
    providedIn: 'root'
})
export class DashboardService {
    gridsterOptions: GridsterConfig;
    onCardAdded = new Subject<DashboardCardModel>();
    onCardRemoved = new Subject<DashboardCardModel>();

    readonly componentsList: DictionaryType<DashboardCardModel> = {};

    get cards(): Subject<DashboardCardModel[]> {
        return this.subscribableCards;
    }

    private isInEditModeSubject = new Subject<boolean>();
    private subscribableCards = new Subject<DashboardCardModel[]>();
    private localCards: DashboardCardModel[] = [];
    private editLayoutDefaultSettingsRoleId?: number;
    private layoutKey: string;
    private fixedConfig?: DashboardCardConfigModel[];

    private readonly layoutSettingsService = inject(LayoutSettingsService);
    private readonly rolesResource = inject(RolesResource);
    private readonly notificationService = inject(NotificationService);
    private readonly markdownComponentService = inject(MarkdownComponentService);

    constructor() {
        this.gridsterOptions = {
            gridType: GridType.Fixed,
            displayGrid: DisplayGrid.None,
            enableEmptyCellContextMenu: true,
            fixedRowHeight: 45,
            fixedColWidth: 75,
            setGridSize: false,
            minCols: 7,
            minRows: 7,
            pushItems: true,
            outerMargin: true,
            margin: 5
        };
        this.setupComponentsList();
    }

    init(
        editLayoutDefaultSettingsRoleId: OptionalType<number> = undefined,
        layoutKey = 'layout',
        fixedConfig?: DashboardCardConfigModel[]
    ): void {
        this.layoutKey = layoutKey;
        this.editLayoutDefaultSettingsRoleId = editLayoutDefaultSettingsRoleId;
        this.fixedConfig = fixedConfig;

        // make sure no cards are still present from a previous dashboard
        this.localCards = [];

        this.loadInitial();
    }

    getEditModeStatus(): Observable<boolean> {
        return this.isInEditModeSubject.asObservable();
    }

    updateEditMode(isInEditMode: boolean): void {
        this.isInEditModeSubject.next(isInEditMode);
    }

    addCardByType(componentType: DashboardComponentTypeEnum, position?: GridsterItemConfig): void {
        // when adding new cards, call the constructor and pass a guid
        const card = new DashboardCardModel(this.componentsList[componentType]!, uuidv4());

        const existingIndex = this.localCards.findIndex(c => c.type === card.type);

        if (existingIndex >= 0 && !card.allowMultipleInstances) {
            this.notificationService.showAlert('Dashboard.WidgetAlreadyAdded');
            return;
        }

        this.addCard(card, position);
    }

    addCard(card: DashboardCardModel, position?: GridsterItemConfig): void {
        if (!!position) {
            card.defaultStartX = position.x;
            card.defaultStartY = position.y;
        }

        this.localCards.push(card);
        this.onCardAdded.next(card);
        this.subscribableCards.next(this.localCards);
    }

    removeCard(card: DashboardCardModel): void {
        const idx = this.localCards.findIndex(c => c.id === card.id);

        this.localCards.splice(idx, 1);
        this.onCardRemoved.next(card);
        this.subscribableCards.next(this.localCards);

        // for markdown components we also need to delete
        // the component instance from the db
        if (card.type === DashboardComponentTypeEnum.Markdown) {
            this.markdownComponentService.delete(card.id);
        }
    }

    saveCurrent(): void {
        const configCards: DashboardCardModel[] = [];

        this.localCards.forEach(lc => {
            const nCard = Object.assign({}, lc);
            nCard.component = undefined;

            configCards.push(nCard);
        });

        if (!!this.editLayoutDefaultSettingsRoleId) {
            const layoutDefaultSettingsModel = new LayoutSettingsModel();
            layoutDefaultSettingsModel.dashboard = new DashboardConfigurationModel();
            layoutDefaultSettingsModel.dashboard.cards = configCards;

            this.rolesResource
                .saveLayoutSettings(layoutDefaultSettingsModel, this.editLayoutDefaultSettingsRoleId, this.layoutKey)
                .subscribe();
        } else {
            this.layoutSettingsService.updateDashboard(
                new DashboardConfigurationModel({
                    cards: configCards
                })
            );
        }
    }

    resetCurrent(): void {
        this.clearCards();
        this.layoutSettingsService.resetLayoutSettings().subscribe(() => this.loadInitial());
    }

    clearCards(): void {
        this.localCards = [];
        this.subscribableCards.next(this.localCards);
    }

    private loadInitial(): void {
        if (!!this.fixedConfig) {
            const config = new DashboardConfigurationModel({
                cards: this.fixedConfig.map(c => {
                    const card = this.componentsList[c.type]!;
                    card.gridsterItem.x = c.x;
                    card.gridsterItem.y = c.y;
                    card.gridsterItem.cols = c.width;
                    card.gridsterItem.rows = c.height;
                    return card;
                })
            });
            this.handleDashboardConfig(config);
        } else if (!!this.editLayoutDefaultSettingsRoleId) {
            this.rolesResource.getLayoutSettings(this.editLayoutDefaultSettingsRoleId, this.layoutKey).subscribe(s => {
                this.handleDashboardConfig(s.dashboard);
            });
        } else {
            this.layoutSettingsService.getLayoutSettings().subscribe(s => {
                if (!!s.dashboard) {
                    this.handleDashboardConfig(s.dashboard);
                }
            });
        }
    }

    private handleDashboardConfig(dashboardConfig: DashboardConfigurationModel): void {
        const newCards: DashboardCardModel[] = [];

        dashboardConfig.cards.forEach(c => {
            if (!!this.componentsList[c.type]) {
                c.component = this.componentsList[c.type]!.component;
                newCards.push(c);
            }
        });

        this.localCards = newCards;
        this.subscribableCards.next(this.localCards);
    }

    private setupComponentsList(): void {
        this.componentsList[DashboardComponentTypeEnum.Top10Shareholders] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetTopNShareholdersCommand',
            type: DashboardComponentTypeEnum.Top10Shareholders,
            defaultColumns: 7,
            defaultRows: 7,
            minColumns: 5,
            minRows: 5,
            component: TopShareholdersComponent
        });
        this.componentsList[DashboardComponentTypeEnum.Top10ShareholdersChart] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetTopNShareholdersCommand',
            type: DashboardComponentTypeEnum.Top10ShareholdersChart,
            defaultColumns: 7,
            defaultRows: 7,
            minColumns: 4,
            minRows: 4,
            component: TopShareholdersChartComponent
        });
        this.componentsList[DashboardComponentTypeEnum.Top10Transactions] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetTopNTransactionsCommand',
            type: DashboardComponentTypeEnum.Top10Transactions,
            defaultColumns: 7,
            defaultRows: 7,
            minColumns: 4,
            minRows: 4,
            component: TopTransactionsComponent
        });
        this.componentsList[DashboardComponentTypeEnum.PendingTransactions] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'ListTransactionsCommand',
            type: DashboardComponentTypeEnum.PendingTransactions,
            defaultColumns: 10,
            defaultRows: 7,
            minColumns: 4,
            minRows: 4,
            component: PendingTransactionsComponent
        });
        this.componentsList[DashboardComponentTypeEnum.TotalDispo] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetTotalDispoCommand',
            type: DashboardComponentTypeEnum.TotalDispo,
            component: TotalDispoComponent,
            defaultColumns: 2,
            defaultRows: 2
        });
        this.componentsList[DashboardComponentTypeEnum.TotalDispoPercent] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetTotalDispoPercentCommand',
            type: DashboardComponentTypeEnum.TotalDispoPercent,
            component: TotalDispoPercentComponent,
            defaultColumns: 2,
            defaultRows: 2
        });
        this.componentsList[DashboardComponentTypeEnum.TotalRegistered] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetTotalRegisteredSharesCommand',
            type: DashboardComponentTypeEnum.TotalRegistered,
            component: TotalRegisteredComponent,
            defaultColumns: 2,
            defaultRows: 2
        });
        this.componentsList[DashboardComponentTypeEnum.TotalRegisteredPercent] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetTotalRegisteredSharesPercentCommand', // TODO: what will be the required permission?
            type: DashboardComponentTypeEnum.TotalRegisteredPercent,
            component: TotalRegisteredPercentComponent,
            defaultColumns: 2,
            defaultRows: 2
        });
        this.componentsList[DashboardComponentTypeEnum.TotalShareholders] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetShareholderCountCommand',
            type: DashboardComponentTypeEnum.TotalShareholders,
            component: TotalShareholdersComponent,
            defaultColumns: 2,
            defaultRows: 2
        });
        this.componentsList[DashboardComponentTypeEnum.Favorites] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetMyFavouritesCommand',
            type: DashboardComponentTypeEnum.Favorites,
            component: FavoritesListComponent,
            defaultColumns: 7,
            defaultRows: 7,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.FileSharing] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetFileSharesCommand',
            type: DashboardComponentTypeEnum.FileSharing,
            component: FileSharingComponent,
            defaultColumns: 7,
            defaultRows: 7,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.GeneralMeetingRegistration] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote,
            commandName: 'UpdateGmShareholderRegisterCommand',
            type: DashboardComponentTypeEnum.GeneralMeetingRegistration,
            component: GeneralMeetingRegistrationComponent,
            defaultColumns: 5,
            defaultRows: 6,
            minColumns: 4,
            minRows: 6
        });
        this.componentsList[DashboardComponentTypeEnum.GeneralMeetingSettings] = new DashboardCardModel({
            dashboards: DashboardEnum.GeneralMeeting,
            commandName: 'GetGeneralMeetingSummaryCommand',
            type: DashboardComponentTypeEnum.GeneralMeetingSettings,
            component: GeneralMeetingSettingsComponent,
            defaultColumns: 6,
            defaultRows: 6,
            minColumns: 3,
            minRows: 3
        });
        this.componentsList[DashboardComponentTypeEnum.GeneralMeetingInfo] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote,
            commandName: 'GetGeneralMeetingSummaryCommand',
            type: DashboardComponentTypeEnum.GeneralMeetingInfo,
            component: GeneralMeetingInfoComponent,
            defaultColumns: 6,
            defaultRows: 6,
            minColumns: 3,
            minRows: 3
        });
        this.componentsList[DashboardComponentTypeEnum.PersonalInfo] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote,
            commandName: 'GetGmShareholderSummaryCommand',
            type: DashboardComponentTypeEnum.PersonalInfo,
            component: PersonalInfoComponent,
            defaultColumns: 5,
            defaultRows: 6,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.Infomaterial] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote,
            commandName: 'GetShareholderInfomaterialCommand',
            type: DashboardComponentTypeEnum.Infomaterial,
            component: InfomaterialComponent,
            defaultColumns: 4,
            defaultRows: 4,
            minColumns: 2,
            minRows: 2
        });
        this.componentsList[DashboardComponentTypeEnum.ShareholderCategories] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetShareholderByTypeCommand',
            type: DashboardComponentTypeEnum.ShareholderCategories,
            component: ShareholderCategoriesComponent,
            defaultColumns: 7,
            defaultRows: 7,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.FactsAndFigures] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetSecuritySummaryCommand',
            type: DashboardComponentTypeEnum.FactsAndFigures,
            component: FactsAndFiguresComponent,
            defaultColumns: 7,
            defaultRows: 7,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.ShareholdersDomicile] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetDomicileStatisticCommand',
            type: DashboardComponentTypeEnum.ShareholdersDomicile,
            component: ShareholdersDomicileComponent,
            defaultColumns: 7,
            defaultRows: 5,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.DeliveryMethodsStatistic] = new DashboardCardModel({
            dashboards: DashboardEnum.Sharelog,
            commandName: 'GetDeliveryMethodStatisticCommands',
            type: DashboardComponentTypeEnum.DeliveryMethodsStatistic,
            component: DeliveryMethodsStatisticComponent,
            defaultColumns: 7,
            defaultRows: 7,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.Chat] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote,
            commandName: 'GetGmChatMessagesCommand',
            type: DashboardComponentTypeEnum.Chat,
            component: ChatComponent,
            defaultColumns: 6,
            defaultRows: 6,
            minColumns: 6,
            minRows: 6
        });
        this.componentsList[DashboardComponentTypeEnum.ChatControl] = new DashboardCardModel({
            dashboards: DashboardEnum.GeneralMeeting | DashboardEnum.Sharelog,
            commandName: 'GetGmChatMessagesCommand',
            type: DashboardComponentTypeEnum.ChatControl,
            component: ChatControlComponent,
            defaultColumns: 11,
            defaultRows: 10,
            minColumns: 10,
            minRows: 9
        });
        this.componentsList[DashboardComponentTypeEnum.LiveVoting] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote,
            commandName: 'VoteGmAgendaItemCommand',
            type: DashboardComponentTypeEnum.LiveVoting,
            component: LiveVotingComponent,
            defaultColumns: 6,
            defaultRows: 6,
            minColumns: 6,
            minRows: 6
        });
        this.componentsList[DashboardComponentTypeEnum.GeneralMeetingAgenda] = new DashboardCardModel({
            dashboards: DashboardEnum.GeneralMeeting,
            commandName: 'GetGmAgendaItemsCommand',
            type: DashboardComponentTypeEnum.GeneralMeetingAgenda,
            component: GeneralMeetingAgendaComponent,
            defaultColumns: 6,
            defaultRows: 6,
            minColumns: 6,
            minRows: 5
        });
        this.componentsList[DashboardComponentTypeEnum.DeliveryMethod] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote,
            commandName: 'UpdateDeliveryMethodCommand',
            type: DashboardComponentTypeEnum.DeliveryMethod,
            component: DeliveryMethodComponent,
            defaultColumns: 4,
            defaultRows: 4,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.Markdown] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote | DashboardEnum.Sharelog,
            commandName: 'GetUserInfoCommand',
            type: DashboardComponentTypeEnum.Markdown,
            component: MarkdownComponent,
            defaultColumns: 6,
            defaultRows: 6,
            minColumns: 2,
            minRows: 3,
            allowMultipleInstances: true
        });
        this.componentsList[DashboardComponentTypeEnum.Presence] = new DashboardCardModel({
            dashboards: DashboardEnum.GeneralMeeting,
            commandName: 'GetGmPresenceNotificationCommand',
            type: DashboardComponentTypeEnum.Presence,
            component: PresenceComponent,
            defaultColumns: 3,
            defaultRows: 6,
            minColumns: 3,
            minRows: 6
        });
        this.componentsList[DashboardComponentTypeEnum.LiveStreamVideo] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote | DashboardEnum.GeneralMeeting,
            commandName: 'GetUserInfoCommand', // TODO: what will be the required permission?
            type: DashboardComponentTypeEnum.LiveStreamVideo,
            component: LiveStreamVideoComponent,
            defaultColumns: 7,
            defaultRows: 8,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.LiveStream] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote | DashboardEnum.GeneralMeeting,
            commandName: 'GetUserInfoCommand', // TODO: what will be the required permission?
            type: DashboardComponentTypeEnum.LiveStream,
            component: LiveStreamComponent,
            defaultColumns: 7,
            defaultRows: 8,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.VotingMonitor] = new DashboardCardModel({
            dashboards: DashboardEnum.GeneralMeeting,
            commandName: 'GetGmVoteMonitorCommand',
            type: DashboardComponentTypeEnum.VotingMonitor,
            component: VotingMonitorComponent,
            defaultColumns: 6,
            defaultRows: 6,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.VirtualSpeakersDesk] = new DashboardCardModel({
            dashboards: DashboardEnum.Netvote,
            commandName: 'SubmitVirtualDeskQuestionCommand',
            type: DashboardComponentTypeEnum.VirtualSpeakersDesk,
            component: VirtualSpeakersDeskComponent,
            defaultColumns: 5,
            defaultRows: 6,
            minColumns: 4,
            minRows: 4
        });
        this.componentsList[DashboardComponentTypeEnum.DmiIntegration] = new DashboardCardModel({
            dashboards: DashboardEnum.GeneralMeeting,
            commandName: 'UpdateDmiSettingsCommand',
            type: DashboardComponentTypeEnum.DmiIntegration,
            component: DmiIntegrationComponent,
            defaultColumns: 5,
            defaultRows: 6,
            minColumns: 5,
            minRows: 5
        });
    }
}
